import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Logo from './Logo';
import { supabase } from '@/lib/supabase';

const footerLinks = [
  {
    title: 'Company',
    links: [
      { label: 'About Us', path: '/about' },
      { label: 'Mission & Vision', path: '/mission-vision' },
      { label: 'Investment', path: '/investment' },
      { label: 'Partnership', path: '/agents-request' },
      { label: 'Careers', path: '/careers' },
    ],
  },
  {
    title: 'Services',
    links: [
      { label: 'Money Transfer', path: '/services' },
      { label: 'Bill Payments', path: '/services' },
      { label: 'Digital Cards', path: '/services' },
      { label: 'Scan to Pay', path: '/services' },
    ],
  },
  {
    title: 'Support',
    links: [
      { label: 'Help Center', path: '/support' },
      { label: 'Security', path: '/security' },
      { label: 'Privacy Policy', path: '/privacy' },
      { label: 'Terms of Service', path: '/terms' },
    ],
  },
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [statusMsg, setStatusMsg] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const formEl = e.currentTarget as HTMLFormElement;
    const formDataObj = new FormData(formEl);

    const honeypot = formDataObj.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      setStatusMsg('Thank you for subscribing.');
      return;
    }

    if (!email || !email.includes('@')) {
      setStatus('error');
      setStatusMsg('Please enter a valid email address.');
      return;
    }

    setStatus('submitting');

    try {
      formDataObj.delete('website_alt');

      const res = await fetch('https://readdy.ai/api/form/d9fqud1gav9vgaom0k00', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataObj as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        // Also save to Supabase for admin panel
        try {
          await supabase.from('newsletter_subscribers').insert({ email });
        } catch {
          // Silently fail — Readdy form already succeeded
        }

        setStatus('success');
        setStatusMsg('Thank you for subscribing.');
        setEmail('');
        formEl.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg && serverMsg.toLowerCase().includes('spam')) {
          setStatus('success');
          setStatusMsg('Thank you for subscribing.');
        } else {
          setStatus('error');
          setStatusMsg(serverMsg || 'Something went wrong. Please try again.');
        }
      }
    } catch {
      setStatus('error');
      setStatusMsg('Something went wrong. Please try again.');
    }
  };

  return (
    <footer className="bg-background-100 border-t border-primary-700/20">
      {/* Main Footer Content */}
      <div className="px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Logo size="sm" linkToHome className="mb-4" />
            <p className="text-foreground-400 text-sm leading-relaxed max-w-sm mb-6 font-body">
              Fast. Secure. Trusted. Your Money. Your Trust. Our Priority. The premier digital wallet solution for the Middle East, Africa, and Europe.
            </p>
          </div>

          {/* Link Columns */}
          {footerLinks.map((column) => (
            <div key={column.title}>
              <h4 className="font-label text-sm font-semibold text-foreground-100 tracking-wider uppercase mb-4">
                {column.title}
              </h4>
              <ul className="space-y-3">
                {column.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.path}
                      className="text-foreground-400 text-sm hover:text-primary-400 transition-colors font-body"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter Section */}
        <div className="mt-12 pt-10 border-t border-background-200/30">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <h4 className="font-heading text-2xl md:text-3xl text-foreground-100 mb-2">
                Stay Updated
              </h4>
              <p className="text-foreground-400 text-sm font-body">
                Get the latest news on features, security updates, and exclusive offers.
              </p>
            </div>
            <form id="newsletter-form" data-readdy-form onSubmit={handleSubmit} className="w-full lg:w-auto flex flex-col sm:flex-row gap-3">
              <div className="hp-field">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full sm:w-72 px-4 py-3 bg-background-50 border border-primary-700/40 rounded-full text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
              />
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="px-6 py-3 bg-primary-500 text-background-50 rounded-full font-label text-sm font-medium tracking-wider hover:bg-primary-400 transition-colors flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === 'submitting' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Subscribing...
                  </>
                ) : (
                  <>
                    Subscribe
                    <i className="ri-arrow-right-line" />
                  </>
                )}
              </button>
            </form>
          </div>
          {status !== 'idle' && status !== 'submitting' && (
            <p className={`mt-3 text-sm font-body ${status === 'success' ? 'text-primary-400' : 'text-red-400'}`}>
              {statusMsg}
            </p>
          )}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="px-4 md:px-8 lg:px-12 py-6 border-t border-background-200/20">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-foreground-500 text-xs font-body">
            &copy; {new Date().getFullYear()} Trusted Money Transfer. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-foreground-500 text-xs hover:text-primary-400 transition-colors font-body">
              Privacy
            </Link>
            <Link to="/terms" className="text-foreground-500 text-xs hover:text-primary-400 transition-colors font-body">
              Terms
            </Link>
            <Link to="/cookies" className="text-foreground-500 text-xs hover:text-primary-400 transition-colors font-body">
              Cookies
            </Link>
            <Link
              to="/admin/settings"
              className="text-foreground-500/50 hover:text-primary-400 transition-colors leading-none"
              title="Admin Panel"
            >
              <i className="ri-settings-3-line text-xs" />
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}